import { createContext, FC, ReactNode, useContext, useMemo, useRef } from "react";
import { useFilterContext } from "@app/network-portfolio-management/contexts/FilterContext";
import { useNPMView } from "@app/network-portfolio-management/hooks/useNPMView";
import { useFilterOptions } from "@app/network-portfolio-management/hooks/useFilterOptions";
import { useGetProjectResources } from "@app/network-portfolio-management/services/npmQueries";
import { useDebouncedValue } from "@app/shared/hooks/useDebouncedValue";
import { UserContext } from "@app/user/UserContext";
import { NetworkPortfolioManagementResourceView, ProjectResourcesRequest } from "@app/network-portfolio-management/interfaces";
import { buildProjectResourcesRequest } from "@app/network-portfolio-management/components/filterBarHelpers";
import { ResourceType } from "@app/resources/resource";

const FILTER_REFETCH_DEBOUNCE_MS = 1000;

interface BaseContextProps {
  loading: boolean;
  projectIds: number[];
  /**
   * IDs of the zip projects in the filtered cohort. Bulk Run/Publish partitions the selection against this
   * set to route zip vs. custom, so Select All routes correctly even for pages that were never loaded.
   */
  zipProjectIds: Set<number>;
  resourcesError: string | null;
}

const defaultContext: BaseContextProps = {
  loading: true,
  projectIds: [],
  resourcesError: null,
  zipProjectIds: new Set(),
};

export const BaseContext = createContext<BaseContextProps>(defaultContext);

interface BaseContextProviderProps {
  children: ReactNode;
}

export const BaseContextProvider: FC<BaseContextProviderProps> = ({ children }) => {
  const userId = useRef(useContext(UserContext).userId).current;

  const { activeFilters } = useFilterContext();
  const view = useNPMView().getCurrentNpmView();
  const { filterOptions } = useFilterOptions();
  const debouncedFilters = useDebouncedValue(activeFilters, FILTER_REFETCH_DEBOUNCE_MS);

  // Resolve the request body outside the query so the query stays a thin transport call: Active and Hidden both
  // carry the current filter set (Hidden is a per-user overlay on the same filtered cohort as Active); only
  // Archived is an unfiltered scope, so any filter state carried over to it is ignored. The view itself
  // travels separately as a query parameter.
  const request = useMemo<ProjectResourcesRequest>(
    () => (view === NetworkPortfolioManagementResourceView.Archived
      ? {}
      : buildProjectResourcesRequest(debouncedFilters, filterOptions)),
    [view, debouncedFilters, filterOptions],
  );
  // JSON.stringify collapses content-identical request objects to the same string key so React Query skips a
  // re-fetch when the user toggles a filter on then immediately back off.
  const requestKey = useMemo(() => JSON.stringify(request), [request]);

  const { data, isLoading, error } = useGetProjectResources(userId, request, view, requestKey);

  // projectIds is the flat id list consumed by the project views; zipProjectIds is the routing lookup for bulk
  // Run/Publish. Both derive from the same query result, so the full cohort - including unloaded pages - is the
  // single source of truth for both.
  const projectIds = useMemo(() => (data ?? []).map((r) => r.resourceId), [data]);
  const zipProjectIds = useMemo(
    () => new Set((data ?? []).filter((r) => r.resourceType === ResourceType.ZipAnalysisProject).map((r) => r.resourceId)),
    [data],
  );

  const value: BaseContextProps = {
    loading: isLoading,
    projectIds,
    resourcesError: error
      ? "Failed to load, please try again. Contact Quest Client Services for assistance if error persists."
      : null,
    zipProjectIds,
  };

  return (
    <BaseContext.Provider value={value}>
      {children}
    </BaseContext.Provider>
  );
};
